import { search } from "algolia-instant-search";
const { stats, configure } = instantsearch.widgets;
const { connectSearchBox } = instantsearch.connectors;
const virtualSearchBox = connectSearchBox(() => {});

const YMM_PARAMS = ["year", "make", "engine"];

function escapeAlgoliaFilterValue(value) {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function getStoredYMMParams() {
  try {
    const storedVehicle = JSON.parse(
      window.sessionStorage.getItem("yme_result_vehicle") || "null",
    );

    if (storedVehicle) {
      return YMM_PARAMS.reduce((params, key) => {
        const value = storedVehicle[key]?.toString().trim();
        if (value) params.set(key, value);
        return params;
      }, new URLSearchParams());
    }

    // garageSelected is legacy teifi/garage state (that system is disabled). The app YME
    // form (yme_result_vehicle) is the single source of truth — do NOT fall back to it,
    // or a stale value strands a "removed" vehicle and filters search to the wrong fitment.
    return new URLSearchParams();
  } catch (error) {
    console.error("Error reading stored YMM selection:", error);
    return new URLSearchParams();
  }
}

function getActiveYMMParams(urlParams) {
  if (YMM_PARAMS.every((param) => urlParams.get(param))) return urlParams;
  return getStoredYMMParams();
}

function getYMMFitmentFilter(urlParams) {
  const activeYMMParams = getActiveYMMParams(urlParams);
  const vehicleParts = YMM_PARAMS.map((param) => activeYMMParams.get(param));
  if (vehicleParts.some((value) => !value)) return "";

  const vehicle = escapeAlgoliaFilterValue(vehicleParts.join(" "));
  return `AND (metafield_fitment_vehicles:"${vehicle}" OR metafield_fitment_universal:true)`;
}

if (!customElements.get("main-search")) {
  class MainSearch extends HTMLElement {
    connectedCallback() {
      this.prepareThemeEditorSearch();
      this.selectElements();

      const urlParams = new URLSearchParams(window.location.search);
      const searchTerms =
        this.data?.search_terms?.trim() || urlParams.get("q")?.trim() || "";
      const catalogIdsFilter = this.data?.catalog_gids
        ? `AND catalog_ids:${this.data.catalog_gids.join(",")}`
        : "";
      const ymmFitmentFilter = getYMMFitmentFilter(urlParams);
      const baseFilters = `status:ACTIVE AND NOT metafield_seo_hidden:1 ${catalogIdsFilter}`;
      const infiniteHits = this.querySelector("algolia-infinite-hits");
      if (infiniteHits) {
        infiniteHits.dataset.hasFitmentFilter = ymmFitmentFilter
          ? "true"
          : "false";
        infiniteHits.dataset.baseFilters = baseFilters;
        // Pass the raw fitment filter so empty-state recommendation rails can stay vehicle-scoped.
        infiniteHits.dataset.fitmentFilter = ymmFitmentFilter;
      }
      const catalogPriceKeys =
        this.data?.catalog_gids?.map((id) => `price_catalog_${id}`) ?? [];

      search.addWidgets([
        virtualSearchBox({}),
        stats({
          container: "#stats",
          templates: {
            text(data, { html }) {
              const { nbHits, page, hitsPerPage } = data;

              if (nbHits === 0)
                return (
                  document.querySelector("#main-product-grid")?.dataset
                    .emptyHeading || "No results"
                );

              const first = page * hitsPerPage + 1;
              const last = Math.min((page + 1) * hitsPerPage, nbHits);

              const querySuffix = searchTerms ? ` for ${searchTerms}` : "";
              const statusText = `${nbHits.toLocaleString()} results (showing ${first}-${last})${querySuffix}`;

              return html`${statusText}`;
            },
          },
        }),
        configure({
          filters: `${baseFilters} ${ymmFitmentFilter}`,
          optionalFilters: [
            "popularity_tier:HIGH<score=3>",
            "popularity_tier:MID<score=2>",
            "is_quickship_stale:true<score=2>",
          ],
          sumOrFiltersScores: true,
          // WHY: with a vehicle fitment filter active, a query that matches nothing within that
          // vehicle's parts must NOT loosen — removeWordsIfNoResults strips the query to one word
          // and returns hundreds of wrong-fit junk. Force 0 → the "Clear Vehicle Fitment" empty
          // state instead. No-fitment searches keep the index-default recall behavior.
          ...(ymmFitmentFilter ? { removeWordsIfNoResults: "none" } : {}),
          distinct: true,
          maxValuesPerFacet: 1000,
          attributesToRetrieve: [
            "handle",
            "title",
            "product_title",
            "featured_image",
            "images",
            "price",
            "compare_at_price",
            "sku",
            "variants",
            "objectID",
            "product_id",
            "id",
            "variant_id",
            "metafield_quick_ship",
            "metafield_expert_pick",
            "metafield_product_availability",
            "metafield_reviews_average",
            "metafield_reviews_count",
            ...catalogPriceKeys,
          ],
        }),
      ]);

      if (document.readyState === "loading") {
        document.addEventListener(
          "DOMContentLoaded",
          () => {
            if (!search.started) search.start();
          },
          { once: true },
        );
      } else if (!search.started) {
        search.start();
      }
    }

    prepareThemeEditorSearch() {
      if (!window.Shopify?.designMode) return;

      const previousElement = window.__dppMainSearchElement;
      if (previousElement && previousElement !== this && search.started) {
        search.dispose();
      }

      window.__dppMainSearchElement = this;
    }

    selectElements() {
      const tag = this.querySelector("script#data");
      try {
        this.data = JSON.parse(tag.textContent);
      } catch (error) {
        console.log(error);
        this.data = {};
      }
    }
  }
  customElements.define("main-search", MainSearch);
}
